import type { ReactNode } from 'react';
import { Icon } from './Icon';

// Standalone right-side drawer (same chrome as the global DrawerHost) for
// feature-local editors like the email template/trigger forms.
export function SideModal({ title, sub, wide, onClose, footer, children }: { title: string; sub?: string; wide?: boolean; onClose: () => void; footer?: ReactNode; children: ReactNode }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(16,24,40,.42)', display: 'flex', justifyContent: 'flex-end', zIndex: 70, animation: 'ccover .15s ease' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: wide ? 640 : 480, maxWidth: '94vw', height: '100%', background: 'var(--surface)', borderLeft: '1px solid var(--border)', boxShadow: 'var(--shadow-md)', display: 'flex', flexDirection: 'column', animation: 'ccslide .2s ease' }}>
        <div style={{ flex: '0 0 auto', padding: '16px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-.01em' }}>{title}</div>
            {sub && <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>}
          </div>
          <button onClick={onClose} className="cc-hover-s2" style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--text-2)', width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="close" size={20} />
          </button>
        </div>
        <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: 18 }}>{children}</div>
        {footer && <div style={{ flex: '0 0 auto', padding: '14px 18px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>{footer}</div>}
      </div>
    </div>
  );
}

const LABEL = { fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase' as const, letterSpacing: '.04em', marginBottom: 7, display: 'block' };
const INPUT = { border: '1px solid var(--border-2)', background: 'var(--surface)', borderRadius: 8, padding: '9px 11px', fontFamily: 'inherit', fontSize: 13, color: 'var(--text)', outline: 'none', width: '100%' } as const;

export function LabeledText({ label, value, onChange, placeholder, mono }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; mono?: boolean }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={LABEL}>{label}</label>
      <input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={mono ? 'cc-mono' : undefined} style={INPUT} />
    </div>
  );
}

export function LabeledArea({ label, value, onChange, placeholder, rows = 6 }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; rows?: number }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={LABEL}>{label}</label>
      <textarea value={value} placeholder={placeholder} rows={rows} onChange={(e) => onChange(e.target.value)} style={{ ...INPUT, resize: 'vertical', lineHeight: 1.6 }} />
    </div>
  );
}

export function LabeledSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { label: string; value: string }[] }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={LABEL}>{label}</label>
      <div style={{ position: 'relative' }}>
        <select value={value} onChange={(e) => onChange(e.target.value)} style={{ ...INPUT, appearance: 'none', cursor: 'pointer', paddingRight: 32 }}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <Icon name="expand_more" size={18} color="var(--text-3)" style={{ position: 'absolute', right: 10, top: 10, pointerEvents: 'none' }} />
      </div>
    </div>
  );
}

export const modalLabelStyle = LABEL;
